// TP-Last-Minute presentational helpers.
//
// Salary band REUSES the Scout fee-frame model: presets are Custom-with-fixed
// values that emit the SAME payload shape FeeFrameSelect already produces
// ({fee_frame_kind, fee_frame_currency, fee_frame_min, fee_frame_max}); the
// composer maps that onto the listing's salary_band_* fields on submit. Salary is
// ALWAYS a monthly band, never an exact figure (Invariant 6).
import { formatFeeAmount } from "./scoutFeeFrame";
import { daysToExpiry } from "./contractRadar";

export { daysToExpiry };

// Design 9e / 4b preset list (monthly bands, copy verbatim incl. the "/ mo").
export const SALARY_BAND_PRESETS = [
  { id: "10_20k", label: "€10-20k / mo", payload: { fee_frame_kind: "range", fee_frame_currency: "EUR", fee_frame_min: 10000, fee_frame_max: 20000 } },
  { id: "20_40k", label: "€20-40k / mo", payload: { fee_frame_kind: "range", fee_frame_currency: "EUR", fee_frame_min: 20000, fee_frame_max: 40000 } },
  { id: "40_60k", label: "€40-60k / mo", payload: { fee_frame_kind: "range", fee_frame_currency: "EUR", fee_frame_min: 40000, fee_frame_max: 60000 } },
  { id: "60_100k", label: "€60-100k / mo", payload: { fee_frame_kind: "range", fee_frame_currency: "EUR", fee_frame_min: 60000, fee_frame_max: 100000 } },
  { id: "100k_plus", label: "€100k+ / mo", payload: { fee_frame_kind: "range", fee_frame_currency: "EUR", fee_frame_min: 100000, fee_frame_max: null } },
];

// Map a FeeFrameSelect value → the listing salary_band_* payload the write API
// accepts (dashboard/last_minute.resolve_salary_band). An empty/open frame clears
// the band.
export const feeFrameToSalaryBand = (v) => {
  if (!v || v.fee_frame_kind !== "range") {
    return { salary_band_kind: "none", salary_band_currency: "", salary_band_min: null, salary_band_max: null };
  }
  return {
    salary_band_kind: "range",
    salary_band_currency: v.fee_frame_currency || "EUR",
    salary_band_min: v.fee_frame_min ?? null,
    salary_band_max: v.fee_frame_max ?? null,
  };
};

// Rebuild a FeeFrameSelect value from a listing's raw salary_band_* fields (edit
// prefill). Returns null when no band is set.
export const salaryBandToFeeFrame = (listing) => {
  if (!listing || listing.salary_band_kind !== "range") return null;
  return {
    fee_frame_kind: "range",
    fee_frame_currency: listing.salary_band_currency || "EUR",
    fee_frame_min: listing.salary_band_min ?? null,
    fee_frame_max: listing.salary_band_max ?? null,
  };
};

// The card/pill salary label. Prefers the server's pre-rendered `salary_band`
// string (feed + mine serializers) and always renders monthly ("… / mo"). Returns
// "" when there is no band so callers hide the pill.
export const formatSalaryBand = (listing) => {
  const body = listing?.salary_band;
  if (!body) return "";
  return `${body} / mo`;
};

// ── Urgency classes (DESIGN-INVENTORY §4 tints — used identically on every card) ─
export const URGENCY = {
  contract_free: { key: "contract_free", label: "Contract-free", bg: "#E6F6EC", text: "#0E7C3C", dot: "#22A75D" },
  expiring_precontract: { key: "expiring_precontract", label: "Expiring & pre-contract", bg: "#FBF1DB", text: "#92650A", dot: "#E3A11C" },
  for_sale: { key: "for_sale", label: "For sale", bg: "#E9F2FF", text: "#1466D8", dot: "#2283FF" },
};
// Expired listings recolour the class pill to neutral grey.
export const URGENCY_EXPIRED = { bg: "#EDF1F6", text: "#71829A", dot: "#98A5B8" };

export const urgencyMeta = (key) => URGENCY[key] || URGENCY.contract_free;

// The three composer options, in design order (4b).
export const URGENCY_OPTIONS = [
  { key: "contract_free", label: "Contract-free", help: "Signable now, even outside windows." },
  { key: "expiring_precontract", label: "Expiring & pre-contract", help: "Running out, open to a pre-contract." },
  { key: "for_sale", label: "For sale", help: "Under contract; open to a transfer." },
];

// ── Market value ─────────────────────────────────────────────────────────────
const CURRENCY_SYMBOL = { EUR: "€", GBP: "£", USD: "$", CHF: "CHF " };

// Named card: exact MV from {amount, currency} → "MV €4.5M". "" when absent.
export const formatMarketValueExact = (mv) => {
  if (!mv || mv.amount === null || mv.amount === undefined) return "";
  const sym = CURRENCY_SYMBOL[String(mv.currency || "EUR").toUpperCase()] || "";
  return `MV ${sym}${formatFeeAmount(mv.amount)}`;
};

// Anonymized card: the server already builds the non-back-identifiable range label
// ("€5M - €7M" / "Under €1M"). We only prefix it. "" when absent.
export const formatMarketValueRange = (mv) => {
  if (!mv || !mv.label) return "";
  return `MV ${mv.label}`;
};

// ── Countdown (owner view, Invariant 8) ──────────────────────────────────────
// Owner expiry descriptor for an ACTIVE listing (design §04/§05 optics):
//   ≤3 days → amber urgency pill "● Expires in N days" + amber card border;
//   otherwise plain "Expires in N days". Same day-granular source as the chat timer.
export const listingCountdown = (listing) => {
  const d = daysToExpiry(listing?.expires_at);
  if (d === null) return { label: "", days: null, urgent: false };
  if (d < 0) return { label: "Expired", days: d, urgent: false };
  const label = `Expires in ${d} day${d === 1 ? "" : "s"}`;
  return { label, days: d, urgent: d <= 3 };
};

// Compact "Nd left" for the browse-card meta line ("2d ago · 12d left").
export const daysLeftShort = (expiresAt) => {
  const d = daysToExpiry(expiresAt);
  if (d === null || d < 0) return "";
  return `${d}d left`;
};

// ── Composer validation (pure, unit-tested) ──────────────────────────────────
// For-sale listings need at least one of buy/loan (server CheckConstraint mirror).
export const forSaleNeedsOption = (urgency, buy, loan) => urgency === "for_sale" && !buy && !loan;

// Step 1 is complete once a subject is picked (+ a role for a staff listing).
export const step1Complete = (listingType, picked, staffRole) =>
  !!picked && (listingType === "player" || !!staffRole);

// ── Position / age meta line ─────────────────────────────────────────────────
export const positionAgeLine = (listing) => {
  const parts = [listing?.position, listing?.age != null ? `${listing.age} yrs` : null].filter(Boolean);
  return parts.join(" · ");
};
